import React, { useState, useEffect, useContext } from "react";
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Stack,
    TextField, FormControl, InputLabel, Select, MenuItem,
    Button,
    IconButton,
    Tooltip,
    Modal,
    CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import ListIcon from "@mui/icons-material/List";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import KeyIcon from "@mui/icons-material/Key";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
// Styled Paper
const StyledPaper = styled(Paper)(({ theme, mode, customColors }) => ({
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
    backgroundColor:
        mode === "dark"
            ? "#2A2A3C"
            : mode === "custom"
                ? customColors?.background || "#f5f5f5"
                : "#f0f7ff",
    borderRadius: "8px",
    boxShadow:
        mode === "dark"
            ? "0 2px 8px rgba(0,0,0,0.6)"
            : "0 2px 8px rgba(0,0,0,0.15)",
}));

// Palette setup
const palette = {
    light: {
        headerBg: "#e3f2fd",
        headerText: "#37474f",
        rowHover: "#f5f5f5",
        text: "#333",
    },
    dark: {
        headerBg: "#2A2A3C",
        headerText: "#EDEDED",
        rowHover: "#383C54",
        text: "#EDEDED",
    },
    custom: (customColors) => ({
        headerBg: customColors?.cardBg || "#f5f5f5",
        headerText: customColors?.text || "#111111",
        rowHover: customColors?.primary || customColors?.primary || "#e8f4ff",
        text: customColors?.text || "#111111",
    }),
};

export default function ListsPanel({
    mode,
    customColors,
    setSnackbar,
    isLoading,
    setIsLoading,
    currentUserRole,
    myPermissions,
}) {
    // ========== STATE ==========
    const [selectedListSubPanel, setSelectedListSubPanel] = useState("list_of_custom_role");
    const [customRoles, setCustomRoles] = useState([]);
    const [customRoleUsers, setCustomRoleUsers] = useState([]);
    const [deleteCustomRoleModalOpen, setDeleteCustomRoleModalOpen] = useState(false);
    const [deleteCustomRoleId, setDeleteCustomRoleId] = useState("");
    const [roleUsersModalOpen, setRoleUsersModalOpen] = useState(false);
    const [roleUsers, setRoleUsers] = useState([]);
    const [selectedRoleName, setSelectedRoleName] = useState("");
    const [deleteCustomRoleUserModalOpen, setDeleteCustomRoleUserModalOpen] = useState(false);
    const [deleteCustomRoleUserId, setDeleteCustomRoleUserId] = useState("");
    const [editCustomRoleUserModalOpen, setEditCustomRoleUserModalOpen] = useState(false);
    const [editCustomRoleUser, setEditCustomRoleUser] = useState({ id: "", email: "", role: "" });
    const [passwordResetModalOpen, setPasswordResetModalOpen] = useState(false);
    const [selectedUserForReset, setSelectedUserForReset] = useState("");
    const [newPassword, setNewPassword] = useState("");
// 🔍 Search states
const [roleSearch, setRoleSearch] = useState("");
const [userSearch, setUserSearch] = useState("");
// ================= PAGINATION STATE (ADDED) =================
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(5);
// ===========================================================

// 🔄 Reset search when switching Roles / Users
useEffect(() => {
  setRoleSearch("");
  setUserSearch("");
  setPage(1); // 🔥 ADD THIS
}, [selectedListSubPanel]);


    const apiUrl = process.env.REACT_APP_BASE_URL;

    // ========== EFFECTS ==========
    // Fetch custom roles when selectedPanel changes
    useEffect(() => {
        if (selectedListSubPanel === "list_of_custom_role") {
            const fetchCustomRoles = async () => {
                setIsLoading(true);
                try {
                    const token = sessionStorage.getItem("authToken");
                    const response = await axios.get(`${apiUrl}/custom-roles/`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    const mappedRoles = Array.isArray(response.data)
                        ? response.data.map((role) => ({
                            id: role.id,
                            name: role.name,
                            permissions: role.permissions || {},
                            created_by: role.created_by,
                        }))
                        : Array.isArray(response.data.roles)
                            ? response.data.roles.map((role) => ({
                                id: role.id,
                                name: role.name,
                                permissions: role.permissions || {},
                                created_by: role.created_by,
                            }))
                            : [];
                    setCustomRoles(mappedRoles);
                } catch (error) {
                    console.error("Failed to fetch custom roles:", error);
                    setSnackbar({
                        open: true,
                        message: error.response?.data?.error || error.response?.data?.message || "Failed to fetch custom roles. Please try again.",
                        severity: "error",
                    });
                    setCustomRoles([]);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchCustomRoles();
        }
    }, [selectedListSubPanel, setIsLoading, setSnackbar]);

    // Fetch custom role users when selectedListSubPanel changes
    useEffect(() => {
        if (selectedListSubPanel === "list_of_custom_role_users") {
            const fetchCustomRoleUsers = async () => {
                setIsLoading(true);
                try {
                    const token = sessionStorage.getItem("authToken");
                    const response = await axios.get(`${apiUrl}/custom-role/users/`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    let mappedUsers = [];
                    if (response.data.custom_roles && typeof response.data.custom_roles === "object") {
                        mappedUsers = Object.entries(response.data.custom_roles).flatMap(([role, users]) =>
                            users.map((user) => ({
                                id: user.id,
                                first_name: user.first_name,
                                last_name: user.last_name,
                                email: user.email,
                                role,
                                is_verified: user.is_verified,
                            }))
                        );
                    }
                    setCustomRoleUsers(mappedUsers);
                } catch (error) {
                    console.error("Failed to fetch custom role users:", error);
                    let errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to fetch custom role users. Please try again.";
                    let severity = error.response?.status === 403 ? "warning" : "error";
                    if (errorMessage === "Permission denied: you cannot can_view_users_of_custom_roles.") {
                        errorMessage = "Permission denied: you do not have permission to view custom role users.";
                        severity = "warning";
                    }
                    setSnackbar({
                        open: true,
                        message: errorMessage,
                        severity: severity,
                    });
                    setCustomRoleUsers([]);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchCustomRoleUsers();
        }
    }, [selectedListSubPanel, setIsLoading, setSnackbar]);
// ================= SEARCH FILTER LOGIC =================

// Custom Roles filter
const filteredCustomRoles = customRoles.filter((role) =>
  role.name?.toLowerCase().includes(roleSearch.toLowerCase())
);

// Custom Role Users filter (name / email / role)
const filteredCustomRoleUsers = customRoleUsers.filter((user) => {
  const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
  return (
    fullName.includes(userSearch.toLowerCase()) ||
    user.email?.toLowerCase().includes(userSearch.toLowerCase()) ||
    user.role?.toLowerCase().includes(userSearch.toLowerCase())
  );
});
// ================= PAGINATION LOGIC (ADDED) =================

// Roles pagination
const paginatedRoles = filteredCustomRoles.slice(
  (page - 1) * rowsPerPage,
  page * rowsPerPage
);

// Users pagination
const paginatedUsers = filteredCustomRoleUsers.slice(
  (page - 1) * rowsPerPage,
  page * rowsPerPage
);

// ===========================================================

    // ========== HANDLERS ==========
    const handleListSubPanelChange = (subPanel) => {
        setSelectedListSubPanel(subPanel);
    };



    const handleOpenDeleteCustomRoleModal = (id) => {
        setDeleteCustomRoleId(id);
        setDeleteCustomRoleModalOpen(true);
    };

    const handleCloseDeleteCustomRoleModal = () => {
        setDeleteCustomRoleModalOpen(false);
        setDeleteCustomRoleId("");
    };

    const handleDeleteCustomRole = async () => {
        try {
            const token = sessionStorage.getItem("authToken");
            const role = customRoles.find((r) => r.id === deleteCustomRoleId);
            if (!role) {
                setSnackbar({
                    open: true,
                    message: "Custom role not found.",
                    severity: "error",
                });
                return;
            }
            await axios.delete(`${apiUrl}/custom-roles/${role.name}/delete/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCustomRoles((prev) => prev.filter((role) => role.id !== deleteCustomRoleId));
            setSnackbar({
                open: true,
                message: "Custom role deleted successfully.",
                severity: "success",
            });
            handleCloseDeleteCustomRoleModal();
        } catch (error) {
            console.error("Failed to delete custom role:", error);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || error.response?.data?.message || "Failed to delete custom role. Please try again.",
                severity: "error",
            });
        }
    };

    const handleOpenRoleUsersModal = async (roleName) => {
        setSelectedRoleName(roleName);
        setIsLoading(true);
        try {
            const token = sessionStorage.getItem("authToken");
            const response = await axios.get(`${apiUrl}/api/users/role/${roleName}/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const mappedUsers = Array.isArray(response.data.users)
                ? response.data.users.map((user) => ({
                    id: user.user_id,
                    email: user.email,
                    name: `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Name not available",
                    role: user.role,
                    is_active: user.is_active,
                    date_joined: user.date_joined,
                }))
                : [];
            setRoleUsers(mappedUsers);
            setRoleUsersModalOpen(true);
            setSnackbar({
                open: true,
                message: `Fetched ${response.data.total_users} user for role ${response.data.role_name}.`,
                severity: "success",
            });
        } catch (error) {
            console.error("Failed to fetch role users:", error);
            const errorMessage =
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.response?.data?.detail ||
                "Failed to fetch users for this role. Please try again.";
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: "error",
            });
            setRoleUsers([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseRoleUsersModal = () => {
        setRoleUsersModalOpen(false);
        setSelectedRoleName("");
        setRoleUsers([]);
    };

    const handleOpenCustomRoleUserEditModal = async (user) => {
        setEditCustomRoleUser({
            id: user.id,
            email: user.email,
            role: user.role,
        });
        setEditCustomRoleUserModalOpen(true);
    };

    const handleCloseCustomRoleUserEditModal = () => {
        setEditCustomRoleUserModalOpen(false);
        setEditCustomRoleUser({ id: "", email: "", role: "" });
    };

    const handleEditCustomRoleUserChange = (key) => (event) => {
        setEditCustomRoleUser((prev) => ({
            ...prev,
            [key]: event.target.value,
        }));
    };

    const handleCustomRoleUserUpdateSubmit = async () => {
        try {
            const token = sessionStorage.getItem("authToken");
            await axios.post(
                `${apiUrl}/custom-roles/assign-to-user/`,
                {
                    user_id: editCustomRoleUser.id,
                    role_name: editCustomRoleUser.role,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCustomRoleUsers((prev) =>
                prev.map((user) =>
                    user.id === editCustomRoleUser.id
                        ? { ...user, email: editCustomRoleUser.email, role: editCustomRoleUser.role }
                        : user
                )
            );
            setSnackbar({
                open: true,
                message: "Custom role user updated successfully.",
                severity: "success",
            });
            handleCloseCustomRoleUserEditModal();
        } catch (error) {
            console.error("Failed to update custom role user:", error);
            if (error.response?.status === 403) {
                setSnackbar({
                    open: true,
                    message: "You do not have permission to change user role.",
                    severity: "warning",
                });
            }
        }
    };

    const handleOpenDeleteCustomRoleUserModal = (id) => {
        setDeleteCustomRoleUserId(id);
        setDeleteCustomRoleUserModalOpen(true);
    };

    const handleCloseDeleteCustomRoleUserModal = () => {
        setDeleteCustomRoleUserModalOpen(false);
        setDeleteCustomRoleUserId("");
    };

    const handleDeleteCustomRoleUser = async () => {
        try {
            const token = sessionStorage.getItem("authToken");
            await axios.delete(`${apiUrl}/users/${deleteCustomRoleUserId}/delete/`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setCustomRoleUsers((prev) => prev.filter((user) => user.id !== deleteCustomRoleUserId));
            setSnackbar({
                open: true,
                message: "Custom role user deleted successfully.",
                severity: "success",
            });
            handleCloseDeleteCustomRoleUserModal();
        } catch (error) {
            console.error("Failed to delete custom role user:", error);
            if (error.response?.status === 403) {
                setSnackbar({
                    open: true,
                    message: "You do not have permission to delete user.",
                    severity: "warning",
                });
            }
        }
    };

    const handleOpenPasswordResetModal = (userId) => {
        setSelectedUserForReset(userId);
        setNewPassword("");
        setPasswordResetModalOpen(true);
    };

    const handleClosePasswordResetModal = () => {
        setPasswordResetModalOpen(false);
        setSelectedUserForReset("");
        setNewPassword("");
    };

    const handleSavePassword = async () => {
        if (!newPassword) {
            setSnackbar({
                open: true,
                message: "Please enter a new password.",
                severity: "warning",
            });
            return;
        }
        try {
            const token = sessionStorage.getItem("authToken");
            await axios.post(
                `${apiUrl}/api/users/${selectedUserForReset}/reset-password/`,
                {
                    new_password: newPassword,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setSnackbar({
                open: true,
                message: "Password reset successfully.",
                severity: "success",
            });
            setPasswordResetModalOpen(false);
            setNewPassword("");
        } catch (error) {
            console.error("Failed to reset password:", error);
            let errorMessage = error.response?.data?.error || error.response?.data?.message || "Failed to reset password. Please try again.";
            let severity = "error";
            if (errorMessage === "Permission denied: you cannot reset passwords.") {
                errorMessage = "Permission denied: you do not have permission to reset password.";
                severity = "warning";
            }
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: severity,
            });
        }
    };

    // ========== RENDER ==========
    return (
        <>
            <StyledPaper
                sx={{
                    backgroundColor: mode === "dark"
                        ? palette.dark.headerBg
                        : mode === "custom"
                            ? customColors?.bgNav || "#f5f5f5"
                            : palette.light.headerBg,
                    width: "100%",
                    borderRadius: "16px",
                    boxShadow: mode === "dark"
                        ? "0 8px 24px rgba(0, 0, 0, 0.3)"
                        : "0 8px 24px rgba(30, 139, 175, 0.12)",
                }}
            >
 <Stack
  direction="row"
  alignItems="center"
  sx={{
    mb: 3,
    pb: 2,
    borderBottom:
      mode === "dark"
        ? "2px solid rgba(255,255,255,0.1)"
        : "2px solid rgba(30,139,175,0.15)",
  }}
>
  {/* LEFT : TITLE */}
  <Typography
    variant="h5"
    sx={{
      fontWeight: 700,
      background: "linear-gradient(135deg,#1e3a8a,#1e8bafff)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      minWidth: 220, // 🔥 IMPORTANT
    }}
  >
    {selectedListSubPanel === "list_of_custom_role"
      ? "Custom Roles"
      : "Custom Role Users"}
  </Typography>

  {/* CENTER : SEARCH (PERFECT CENTER) */}
  <Box sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end",mr:3, }}>
    {/* 🔍 SEARCH */}
    <TextField
      size="small"
      placeholder={
        selectedListSubPanel === "list_of_custom_role"
          ? "Search roles..."
          : "Search users..."
      }
      value={
        selectedListSubPanel === "list_of_custom_role"
          ? roleSearch
          : userSearch
      }
      onChange={(e) =>
        selectedListSubPanel === "list_of_custom_role"
          ? setRoleSearch(e.target.value)
          : setUserSearch(e.target.value)
      }
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon sx={{ color: "#1e8bafff" }} />
          </InputAdornment>
        ),
      }}
      sx={{
        width: 260,
        ml: "auto",          // 🔥 pushes search near buttons
        "& .MuiOutlinedInput-root": {
          borderRadius: "10px",
          "& fieldset": {
            borderColor: "#1e8bafff",
            borderWidth: "2px",
          },
          "&:hover fieldset": {
            borderColor: "#1e8bafff",
          },
          "&.Mui-focused fieldset": {
            borderColor: "#1e8bafff",
          },
        },
      }}
    />



  </Box>

  {/* RIGHT : TOGGLE BUTTONS */}
  <Stack direction="row" spacing={1.5} sx={{ minWidth: 220 }}>
    <Button
      variant={selectedListSubPanel === "list_of_custom_role" ? "contained" : "outlined"}
      onClick={() => setSelectedListSubPanel("list_of_custom_role")}
sx={{
                                    '&:focus': { outline: 'none' },
                                    borderRadius: 3,
                                    background:selectedListSubPanel === "list_of_custom_role"
                                        ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)"
                                        : "transparent",
                                    color: selectedListSubPanel === "list_of_custom_role" 
                                        ? "white"
                                        : mode === "dark"
                                            ? palette.dark.headerText
                                            : mode === "custom"
                                                ? customColors?.text || "#111111"
                                                : "#1e3a8a",
                                    borderColor: selectedListSubPanel === "list_of_custom_role" 
                                        ? "transparent"
                                        : mode === "dark"
                                            ? "rgba(255, 255, 255, 0.3)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    borderWidth: "2px",
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    textTransform: "none",
                                    boxShadow: selectedListSubPanel === "list_of_custom_role"
                                        ? "0 4px 12px rgba(30, 58, 138, 0.3)"
                                        : "none",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: selectedListSubPanel === "list_of_custom_role"
                                            ? "linear-gradient(135deg, #1a3278 0%, #1a7a9a 100%)"
                                            : mode === "dark"
                                                ? "rgba(30, 139, 175, 0.1)"
                                                : "rgba(30, 139, 175, 0.08)",
                                        borderColor: selectedListSubPanel === "list_of_custom_role" 
                                            ? "transparent"
                                            : "rgba(30, 139, 175, 0.5)",
                                        transform: "translateY(-2px)",
                                        boxShadow: selectedListSubPanel === "list_of_custom_role" 
                                            ? "0 6px 16px rgba(30, 58, 138, 0.4)"
                                            : "0 4px 12px rgba(30, 139, 175, 0.2)",
                                    },
                                }}

    >
      Roles
    </Button>
    <Button
      variant={selectedListSubPanel === "list_of_custom_role_users" ? "contained" : "outlined"}
      onClick={() => setSelectedListSubPanel("list_of_custom_role_users")}
  sx={{
                                    '&:focus': { outline: 'none' },
                                    borderRadius: 3,
                                    background: selectedListSubPanel === "list_of_custom_role_users" 
                                        ? "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)"
                                        : "transparent",
                                    color: selectedListSubPanel === "list_of_custom_role_users" 
                                        ? "white"
                                        : mode === "dark"
                                            ? palette.dark.headerText
                                            : mode === "custom"
                                                ? customColors?.text || "#111111"
                                                : "#1e3a8a",
                                    borderColor: selectedListSubPanel === "list_of_custom_role_users"
                                        ? "transparent"
                                        : mode === "dark"
                                            ? "rgba(255, 255, 255, 0.3)"
                                            : "rgba(30, 139, 175, 0.3)",
                                    borderWidth: "2px",
                                    fontWeight: 600,
                                    px: 3,
                                    py: 1,
                                    textTransform: "none",
                                    boxShadow: selectedListSubPanel === "list_of_custom_role_users" 
                                        ? "0 4px 12px rgba(30, 58, 138, 0.3)"
                                        : "none",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        background: selectedListSubPanel === "list_of_custom_role_users" 
                                            ? "linear-gradient(135deg, #1a3278 0%, #1a7a9a 100%)"
                                            : mode === "dark"
                                                ? "rgba(30, 139, 175, 0.1)"
                                                : "rgba(30, 139, 175, 0.08)",
                                        borderColor: selectedListSubPanel === "list_of_custom_role_users" 
                                            ? "transparent"
                                            : "rgba(30, 139, 175, 0.5)",
                                        transform: "translateY(-2px)",
                                        boxShadow: selectedListSubPanel === "list_of_custom_role_users" 
                                            ? "0 6px 16px rgba(30, 58, 138, 0.4)"
                                            : "0 4px 12px rgba(30, 139, 175, 0.2)",
                                    },
                                }}
  >
      Users
    </Button>
  </Stack>
</Stack>



                {/* Render content of the active sub-panel */}
                {selectedListSubPanel === "list_of_custom_role" && (
                    <>
                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <CircularProgress sx={{ color: "#1e8bafff" }} size={48} />
                            </Box>
                        ) : customRoles.length > 0 ? (
                         <>
                            <TableContainer
                             
                           sx={{
                                    width: "100%",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    border: mode === "dark"
                                        ? "1px solid rgba(255, 255, 255, 0.1)"
                                        : "1px solid rgba(30, 139, 175, 0.15)",
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    color: "#ffffff",
                                                    fontWeight: 700,
                                                    fontSize: "0.95rem",
                                                    letterSpacing: "0.5px",
                                                    py: 2,
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                Role Name
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    color: "#ffffff",
                                                    fontWeight: 700,
                                                    fontSize: "0.95rem",
                                                    letterSpacing: "0.5px",
                                                    py: 2,
                                                    textTransform: "uppercase",
                                                    textAlign: "center",
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {/*  {filteredCustomRoles.map((role, index) => ( */}
                                      {paginatedRoles.map((role, index) => (

                                            <TableRow
                                                key={role.id}
                                                sx={{
                                                    backgroundColor: mode === "dark"
                                                        ? index % 2 === 0 ? "rgba(255, 255, 255, 0.03)" : "transparent"
                                                        : index % 2 === 0 ? "rgba(30, 139, 175, 0.03)" : "transparent",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                        backgroundColor: mode === "dark"
                                                            ? "rgba(30, 139, 175, 0.1)"
                                                            : "rgba(30, 139, 175, 0.08)",
                                                        transform: "scale(1.01)",
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            color: mode === "dark"
                                                                ? palette.dark.text
                                                                : mode === "custom"
                                                                    ? customColors?.text || "#1e3a8a"
                                                                    : "#1e3a8a",
                                                            cursor: "pointer",
                                                            fontWeight: 600,
                                                            fontSize: "0.95rem",
                                                            transition: "all 0.2s ease",
                                                            "&:hover": {
                                                                color: "#1e8bafff",
                                                                textDecoration: "underline",
                                                                transform: "translateX(4px)",
                                                            },
                                                        }}
                                                        onClick={() => handleOpenRoleUsersModal(role.name)}
                                                    >
                                                        {role.name?.replace(/[^a-zA-Z0-9\s]/g, '')?.replace(/\s+/g, ' ').trim() || 'Unnamed Role'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: "center" }}>
                                                    <Tooltip title="Delete Role" arrow>
                                                        <span>
                                                            <IconButton
                                                                onClick={() => handleOpenDeleteCustomRoleModal(role.id)}
                                                                disabled={currentUserRole !== "admin" && !myPermissions.can_delete_custom_role}
                                                                sx={{
                                                                    color: currentUserRole !== "admin" && !myPermissions.can_delete_custom_role
                                                                        ? "rgba(0, 0, 0, 0.26)"
                                                                        : "#d32f2f",
                                                                    transition: "all 0.3s ease",
                                                                    "&:hover": {
                                                                        backgroundColor: "rgba(211, 47, 47, 0.1)",
                                                                        transform: "scale(1.15)",
                                                                    },
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
  
{/* ================= ROLES PAGINATION ================= */}
<Box
  sx={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mt: 2,
    pt: 2,
    px: 2,
    borderTop: "1px solid rgba(30,139,175,0.15)",
  }}
>
  <Typography sx={{ fontSize: "0.9rem", opacity: 0.8 }}>
    Showing {(page - 1) * rowsPerPage + 1} to{" "}
    {Math.min(page * rowsPerPage, filteredCustomRoles.length)} of{" "}
    {filteredCustomRoles.length} roles
  </Typography>

  <Stack direction="row" spacing={2} alignItems="center">
   <FormControl size="small">
  <Select
    value={rowsPerPage}
    onChange={(e) => {
      setRowsPerPage(Number(e.target.value));
      setPage(1);
    }}
    sx={{
      borderRadius: "14px",              // ✅ rounded like button
      fontWeight: 600,
      px: 1.5,
      minWidth: 130,
      height: 40,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1e8bafff",
        borderWidth: "2px",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1e8bafff",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#1e8bafff",
      },
      "& .MuiSelect-select": {
        display: "flex",
        alignItems: "center",
        fontSize: "0.9rem",
      },
    }}
  >
    {[5, 10, 20, 50, 100].map((n) => (
      <MenuItem key={n} value={n}>
        {n} per page
      </MenuItem>
    ))}
  </Select>
</FormControl>


   <Button
  variant="outlined"
  disabled={page === 1}
  onClick={() => setPage((p) => p - 1)}
  sx={{
    borderRadius: "14px",   // ✅ rounded corners
    px: 3,
    fontWeight: 600,
    textTransform: "none",
  }}
>
  Prev
</Button>

<Button
  variant="contained"
  disabled={page * rowsPerPage >= filteredCustomRoles.length}
  onClick={() => setPage((p) => p + 1)}
  sx={{
    borderRadius: "14px",   // ✅ rounded corners
    px: 3,
    fontWeight: 600,
    textTransform: "none",
  }}
>
  Next
</Button>

  </Stack>
</Box>
{/* ================= END ROLES PAGINATION ================= */}
</>

                        ) : (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 8,
                                    opacity: 0.7,
                                }}
                            >
                                <ListIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: mode === "dark"
                                            ? palette.dark.text
                                            : "#37474f",
                                        fontWeight: 500,
                                    }}
                                >
                                    No custom roles found
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.5)"
                                            : "rgba(0, 0, 0, 0.5)",
                                        mt: 1,
                                    }}
                                >
                                    Create a custom role to get started
                                </Typography>
                            </Box>
                        )}
                
                    </>
                )}

                {selectedListSubPanel === "list_of_custom_role_users" && (
                    <>
                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                                <CircularProgress sx={{ color: "#1e8bafff" }} size={48} />
                            </Box>
                        ) : customRoleUsers.length > 0 ? (
                        <>
                            <TableContainer
                                sx={{
                                    width: "100%",
                                    borderRadius: "12px",
                                    overflow: "hidden",
                                    border: mode === "dark"
                                        ? "1px solid rgba(255, 255, 255, 0.1)"
                                        : "1px solid rgba(30, 139, 175, 0.15)",
                                }}
                            >
                                <Table>
                                    <TableHead>
                                        <TableRow
                                            sx={{
                                                background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                                            }}
                                        >
                                            {["Name", "Role", "Email", "Actions"].map((header) => (
                                                <TableCell
                                                    key={header}
                                                    sx={{
                                                        color: "#ffffff",
                                                        fontWeight: 700,
                                                        fontSize: "0.95rem",
                                                        letterSpacing: "0.5px",
                                                        py: 2,
                                                        textTransform: "uppercase",
                                                        textAlign: header === "Actions" ? "center" : "left",
                                                    }}
                                                >
                                                    {header}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                       {/* {filteredCustomRoleUsers.map((user, index) => ( */}
                                       {paginatedUsers.map((user, index) => (

                                            <TableRow
                                                key={user.id}
                                                sx={{
                                                    backgroundColor: mode === "dark"
                                                        ? index % 2 === 0 ? "rgba(255, 255, 255, 0.03)" : "transparent"
                                                        : index % 2 === 0 ? "rgba(30, 139, 175, 0.03)" : "transparent",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                        backgroundColor: mode === "dark"
                                                            ? "rgba(30, 139, 175, 0.1)"
                                                            : "rgba(30, 139, 175, 0.08)",
                                                    },
                                                }}
                                            >
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: "0.95rem",
                                                            color: mode === "dark" ? palette.dark.text : "#37474f",
                                                        }}
                                                    >
                                                        {user.first_name || user.last_name
                                                            ? `${user.first_name ? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1).toLowerCase() : ""} ${user.last_name ? user.last_name.charAt(0).toUpperCase() + user.last_name.slice(1).toLowerCase() : ""}`.trim()
                                                            : "Name not available"}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: "inline-block",
                                                            px: 2,
                                                            py: 0.5,
                                                            borderRadius: 2,
                                                            background: "linear-gradient(135deg, #1e3a8a20 0%, #1e8bafff20 100%)",
                                                            border: "1px solid rgba(30, 139, 175, 0.3)",
                                                        }}
                                                    >
                                                        <Typography
                                                            sx={{
                                                                fontWeight: 600,
                                                                fontSize: "0.85rem",
                                                                color: "#1e3a8a",
                                                            }}
                                                        >
                                                            {user.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "0.9rem",
                                                            color: mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.7)",
                                                        }}
                                                    >
                                                        {user.email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: "center" }}>
                                                    <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
                                                        <Tooltip title={user.is_verified ? "Verified" : "Not Verified"} arrow>
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={user.is_verified}
                                                                    sx={{
                                                                        transition: "all 0.3s ease",
                                                                        "&:hover": {
                                                                            transform: "scale(1.15)",
                                                                        },
                                                                    }}
                                                                >
                                                                    {user.is_verified ? (
                                                                        <CheckCircleIcon color="success" />
                                                                    ) : (
                                                                        <CancelIcon color="error" />
                                                                    )}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                        <Tooltip title="Update User" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenCustomRoleUserEditModal(user)}
                                                                disabled={currentUserRole !== "admin" && !myPermissions.can_change_user_role}
                                                                sx={{
                                                                    color: currentUserRole !== "admin" && !myPermissions.can_change_user_role ? "rgba(0, 0, 0, 0.26)" : "#1e8bafff",
                                                                    transition: "all 0.3s ease",
                                                                    "&:hover": {
                                                                        backgroundColor: "rgba(30, 139, 175, 0.1)",
                                                                        transform: "scale(1.15)",
                                                                    },
                                                                }}
                                                            >
                                                                <PersonIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        {/* <Tooltip title="Reset Password" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenPasswordResetModal(user.id)}
                                                                disabled={currentUserRole !== "admin" && !myPermissions.can_change_user_role}
                                                                sx={{
                                                                    color: currentUserRole !== "admin" && !myPermissions.can_change_user_role ? "rgba(0, 0, 0, 0.26)" : "#ff9800",
                                                                    transition: "all 0.3s ease",
                                                                    "&:hover": {
                                                                        backgroundColor: "rgba(255, 152, 0, 0.1)",
                                                                        transform: "scale(1.15)",
                                                                    },
                                                                }}
                                                            >
                                                                <KeyIcon />
                                                            </IconButton>
                                                        </Tooltip> */}
                                                        <Tooltip title="Delete User" arrow>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleOpenDeleteCustomRoleUserModal(user.id)}
                                                                disabled={currentUserRole !== "admin" && !myPermissions.can_delete_user}
                                                                sx={{
                                                                    color: currentUserRole !== "admin" && !myPermissions.can_delete_user ? "rgba(0, 0, 0, 0.26)" : "#d32f2f",
                                                                    transition: "all 0.3s ease",
                                                                    "&:hover": {
                                                                        backgroundColor: "rgba(211, 47, 47, 0.1)",
                                                                        transform: "scale(1.15)",
                                                                    },
                                                                }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
    
    {/* ================= USERS PAGINATION ================= */}
        <Box
            sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 2,
                pt: 2,
                px: 2,
                borderTop: "1px solid rgba(30,139,175,0.15)",
            }}
        >
            <Typography sx={{ fontSize: "0.9rem", opacity: 0.8 }}>
                Showing {(page - 1) * rowsPerPage + 1} to{" "}
                {Math.min(page * rowsPerPage, filteredCustomRoleUsers.length)} of{" "}
                {filteredCustomRoleUsers.length} users
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <FormControl size="small">
  <Select
    value={rowsPerPage}
    onChange={(e) => {
      setRowsPerPage(Number(e.target.value));
      setPage(1);
    }}
    sx={{
      borderRadius: "14px",            // ✅ rounded like buttons
      px: 1,
      fontWeight: 600,
      textTransform: "none",
      minWidth: 130,
      "& .MuiOutlinedInput-notchedOutline": {
        borderRadius: "14px",          // ✅ IMPORTANT
      },
    }}
  >
    {[5, 10, 20, 50, 100].map((n) => (
      <MenuItem key={n} value={n}>
        {n} per page
      </MenuItem>
    ))}
  </Select>
</FormControl>


<Button
  variant="outlined"
  disabled={page === 1}
  onClick={() => setPage((p) => p - 1)}
  sx={{
    borderRadius: "12px",          // 🔥 rounded corners
    px: 3,
    textTransform: "none",
    fontWeight: 600,
  }}
>
  Prev
</Button>

<Button
  variant="contained"
  disabled={page * rowsPerPage >= filteredCustomRoles.length}
  onClick={() => setPage((p) => p + 1)}
  sx={{
    borderRadius: "12px",          // 🔥 rounded corners
    px: 3,
    textTransform: "none",
    fontWeight: 600,
  }}
>
  Next
</Button>

            </Stack>
        </Box>
        {/* ================= END USERS PAGINATION ================= */}
    </>

                        ) : (
                            <Box
                                sx={{
                                    textAlign: "center",
                                    py: 8,
                                    opacity: 0.7,
                                }}
                            >
                                <PeopleIcon sx={{ fontSize: 64, color: "#1e8bafff", mb: 2, opacity: 0.5 }} />
                                <Typography
                                    variant="h6"
                                    sx={{
                                        color: mode === "dark"
                                            ? palette.dark.text
                                            : "#37474f",
                                        fontWeight: 500,
                                    }}
                                >
                                    No custom role users found
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: mode === "dark"
                                            ? "rgba(255, 255, 255, 0.5)"
                                            : "rgba(0, 0, 0, 0.5)",
                                        mt: 1,
                                    }}
                                >
                                    Register users to assign them custom roles
                                </Typography>
                            </Box>
                        )
                      }  </>
                )}
            </StyledPaper>

            {/* Modals */}
            <Modal
                open={deleteCustomRoleModalOpen}
                onClose={handleCloseDeleteCustomRoleModal}
                aria-labelledby="delete-custom-role-modal-title"
                aria-describedby="delete-custom-role-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography
                        id="delete-custom-role-modal-title"
                        variant="h6"
                        sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" }}
                    >
                        Confirm Delete Custom Role
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Are you sure you want to delete this custom role? This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseDeleteCustomRoleModal}
                            sx={{
                                color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                bordercolor: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                textTransform: "none",
                                borderRadius: "8px",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDeleteCustomRole}
                            sx={{
                                backgroundColor: "#d32f2f",
                                color: "white",
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#b71c1c" },
                            }}
                        >
                            Confirm Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={roleUsersModalOpen}
                onClose={handleCloseRoleUsersModal}
                aria-labelledby="role-users-modal-title"
                aria-describedby="role-users-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 600,
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        p: 4,
                        maxHeight: "80vh",
                        overflowY: "auto",
                    }}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography
                            id="role-users-modal-title"
                            variant="h6"
                            sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" }}
                        >
                            Users in {selectedRoleName.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        </Typography>
                        <IconButton onClick={handleCloseRoleUsersModal}>
                            <CloseIcon sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText }} />
                        </IconButton>
                    </Box>
                    <Typography sx={{ color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text, mb: 2 }}>
                        <strong>Total Users:</strong> {roleUsers.length}
                    </Typography>
                    {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                            <CircularProgress sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText }} />
                        </Box>
                    ) : roleUsers.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{
                                        background: "linear-gradient(135deg, #1e3a8a 0%, #1e8bafff 100%)",
                                    }}>
                                        <TableCell sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: 500 }}>Name</TableCell>
                                        <TableCell sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: 500 }}>Email</TableCell>
                                        <TableCell sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: 500 }}>Role</TableCell>
                                        <TableCell sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: 500 }}>Date Joined</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {roleUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>{user.name}</TableCell>
                                            <TableCell>{user.email}</TableCell>
                                            <TableCell>{user.role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}</TableCell>
                                            <TableCell>
                                                {user.date_joined
                                                    ? new Date(user.date_joined).toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    })
                                                    : "Not provided"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography sx={{ textAlign: "center", color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#37474f" : "#37474f" }}>
                            No users found for this role
                        </Typography>
                    )}
                </Box>
            </Modal>

            <Modal
                open={editCustomRoleUserModalOpen}
                onClose={handleCloseCustomRoleUserEditModal}
                aria-labelledby="edit-custom-role-user-modal-title"
                aria-describedby="edit-custom-role-user-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <IconButton
                        onClick={handleCloseCustomRoleUserEditModal}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText }} />
                    </IconButton>
                    <Typography
                        id="edit-custom-role-user-modal-title"
                        variant="h6"
                        sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, mb: 2, fontWeight: "500" }}
                    >
                        Edit Custom Role User
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label="Email"
                            value={editCustomRoleUser.email}
                            onChange={handleEditCustomRoleUserChange("email")}
                            required
                            disabled
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": { backgroundColor: mode === "dark" ? palette.dark.headerBg : mode === "custom" ? customColors?.bg || "#f0f7ff" : "#f0f7ff", borderRadius: "8px" },
                                "& .MuiInputLabel-root": { color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" },
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id="edit-custom-role-select-label" sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" }}>
                                Role
                            </InputLabel>
                            <Select
                                labelId="edit-custom-role-select-label"
                                value={editCustomRoleUser.role}
                                label="Role"
                                onChange={handleEditCustomRoleUserChange("role")}
                                sx={{
                                    backgroundColor: mode === "dark" ? palette.dark.headerBg : mode === "custom" ? customColors?.bg || "#f0f7ff" : "#f0f7ff",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0, 0, 0, 0.23)" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#142be1" },
                                }}
                            >
                                {customRoles.length > 0 ? (
                                    customRoles.map((role) => (
                                        <MenuItem key={role.id} value={role.name}>
                                            {role.name?.replace(/[^a-zA-Z0-9\s]/g, '')?.replace(/\s+/g, ' ').trim() || 'Unnamed Role'}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <MenuItem value="" disabled>
                                        No roles available
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            onClick={handleCustomRoleUserUpdateSubmit}
                            sx={{
                                backgroundColor: "#4caf50",
                                color: "white",
                                fontWeight: 600,
                                textTransform: "none",
                                mt: 2,
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#388e3c" },
                            }}
                        >
                            Update User
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={deleteCustomRoleUserModalOpen}
                onClose={handleCloseDeleteCustomRoleUserModal}
                aria-labelledby="delete-custom-role-user-modal-title"
                aria-describedby="delete-custom-role-user-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography
                        id="delete-custom-role-user-modal-title"
                        variant="h6"
                        sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" }}
                    >
                        Confirm Delete Custom Role User
                    </Typography>
                    <Typography sx={{ mb: 2 }}>
                        Are you sure you want to delete this user? This action cannot be undone.
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseDeleteCustomRoleUserModal}
                            sx={{
                                color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                bordercolor: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                textTransform: "none",
                                borderRadius: "8px",
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleDeleteCustomRoleUser}
                            sx={{
                                backgroundColor: "#d32f2f",
                                color: "white",
                                textTransform: "none",
                                borderRadius: "8px",
                                "&:hover": { backgroundColor: "#b71c1c" },
                            }}
                        >
                            Confirm Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                open={passwordResetModalOpen}
                onClose={handleClosePasswordResetModal}
                aria-labelledby="password-reset-modal-title"
                aria-describedby="password-reset-modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        bgcolor: "background.paper",
                        borderRadius: "8px",
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <IconButton
                        onClick={handleClosePasswordResetModal}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                    >
                        <CloseIcon sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText }} />
                    </IconButton>
                    <Typography
                        id="password-reset-modal-title"
                        variant="h6"
                        sx={{ color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, mb: 2, fontWeight: "500" }}
                    >
                        Reset Password
                    </Typography>
                    <Typography sx={{ mb: 2, color: mode === "dark" ? palette.dark.text : mode === "custom" ? customColors?.text || "#111111" : palette.light.text }}>
                        Enter the new password for the user.
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <TextField
                            label="New Password"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            fullWidth
                            sx={{
                                "& .MuiInputBase-root": { backgroundColor: mode === "dark" ? palette.dark.headerBg : mode === "custom" ? customColors?.bg || "#f0f7ff" : "#f0f7ff", borderRadius: "8px" },
                                "& .MuiInputLabel-root": { color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText, fontWeight: "500" },
                            }}
                        />
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleClosePasswordResetModal}
                                sx={{
                                    color: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                    borderColor: mode === "dark" ? palette.dark.headerText : mode === "custom" ? customColors?.text || "#111111" : palette.light.headerText,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSavePassword}
                                disabled={!newPassword}
                                sx={{
                                    backgroundColor: "#4caf50",
                                    color: "white",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: "8px",
                                    "&:hover": { backgroundColor: "#388e3c" },
                                }}
                            >
                                Save Password
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </>
    );
}
